import React, { useEffect, useState } from "react";
import axios from "axios";
import { Search, X, Trash2, Edit, Check, ArrowUpDown } from "lucide-react";

const AdminUser = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("https://api.kamulyaan.id/adminuser");
        setUsers(
          data.success && Array.isArray(data.users)
            ? data.users
            : Array.isArray(data)
            ? data
            : []
        );
      } catch (err) {
        setError(err.response?.data?.message || "Gagal memuat pengguna");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter (search any field)
  const filtered = users.filter((u) =>
    Object.values(u)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // Sort (string-based)
  const sorted = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = String(a[sortConfig.key] ?? "").toLowerCase();
    const bVal = String(b[sortConfig.key] ?? "").toLowerCase();
    return sortConfig.direction === "asc"
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  });

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortIcon = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "asc" ? "↑" : "↓"
      : <ArrowUpDown size={14} />;

  // Selection helpers
  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAll = () =>
    setSelectedIds(
      selectedIds.size === sorted.length && sorted.length > 0
        ? new Set()
        : new Set(sorted.map((u) => u.userid))
    );

  const clearSelection = () => setSelectedIds(new Set());

  // Edit modal
  const openEdit = (user) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingUser((prev) => ({ ...prev, [name]: value }));
  };

  const saveEdit = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`https://api.kamulyaan.id/adminuser/${editingUser.userid}`, editingUser);
      setUsers((prev) =>
        prev.map((u) => (u.userid === editingUser.userid ? { ...editingUser } : u))
      );
      setShowEditModal(false);
    } catch (err) {
      alert("Gagal update: " + (err.response?.data?.message || err.message));
    }
  };

  // Delete single
  const deleteUser = async (id) => {
    if (!window.confirm("Hapus pengguna ini?")) return;
    try {
      await axios.delete(`https://api.kamulyaan.id/adminuser/${id}`);
      setUsers((prev) => prev.filter((u) => u.userid !== id));
    } catch (err) {
      alert("Gagal hapus: " + (err.response?.data?.message || err.message));
    }
  };

  // Bulk delete
  const bulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Hapus ${selectedIds.size} pengguna?`)) return;
    try {
      await Promise.all(
        [...selectedIds].map((id) => axios.delete(`https://api.kamulyaan.id/adminuser/${id}`))
      );
      setUsers((prev) => prev.filter((u) => !selectedIds.has(u.userid)));
      clearSelection();
    } catch (err) {
      alert("Gagal hapus beberapa pengguna");
    }
  };

  if (loading) return <div className="loading">Memuat...</div>;
  if (error)   return <div className="error">{error}</div>;

  return (
    <div className="admin-page p-4 max-w-full">
      <header className="mb-4">
        <h1 className="text-2xl font-bold mb-3">Manajemen Pengguna</h1>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] flex items-center bg-white border border-gray-300 rounded-full px-3 py-2">
            <Search size={18} className="text-gray-500" />
            <input
              className="flex-1 ml-2 outline-none bg-transparent"
              placeholder="Cari nama, userid, email, telpon, kota..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <X
                size={18}
                className="cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={() => setSearchTerm("")}
              />
            )}
          </div>
        </div>
      </header>

      {selectedIds.size > 0 && (
        <div className="bg-amber-50 p-3 rounded-lg mb-4 flex flex-wrap gap-3 items-center">
          <span>{selectedIds.size} terpilih</span>
          <button onClick={clearSelection} className="px-4 py-1.5 bg-gray-200 rounded">
            Batal
          </button>
          <button
            onClick={bulkDelete}
            className="px-4 py-1.5 bg-red-600 text-white rounded flex items-center gap-1"
          >
            <Trash2 size={16} /> Hapus
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white max-h-[70vh]">
        <table className="w-full min-w-[1100px] border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={selectedIds.size === sorted.length && sorted.length > 0}
                  onChange={selectAll}
                />
              </th>
              {[
                { label: "UserID", key: "userid" },
                { label: "Nama",    key: "nama"   },
                { label: "Level",   key: "level"  },
                { label: "Kota",    key: "kota"   },
                { label: "Telpon",  key: "telpon" },
                { label: "WhatsApp",key: "wa"     },
                { label: "Email",   key: "email"  },
                { label: "Website", key: "web"    },
                { label: "Aksi",    key: null     },
              ].map(({ label, key }) => (
                <th
                  key={label}
                  className={`p-3 text-left font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-200 ${
                    key ? "" : "text-right"
                  }`}
                  onClick={() => key && toggleSort(key)}
                >
                  {label} {key && sortIcon(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center py-16 text-gray-500">
                  Tidak ada data pengguna
                </td>
              </tr>
            ) : (
              sorted.map((u) => {
                const selected = selectedIds.has(u.userid);
                return (
                  <tr key={u.userid} className={selected ? "bg-blue-50" : "hover:bg-gray-50"}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleSelect(u.userid)}
                      />
                    </td>
                    <td className="p-3 font-medium">{u.userid}</td>
                    <td className="p-3 max-w-xs truncate">{u.nama || "—"}</td>
                    <td className="p-3">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full ${
                          u.level?.toLowerCase().includes("admin")
                            ? "bg-purple-100 text-purple-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {u.level || "—"}
                      </span>
                    </td>
                    <td className="p-3">{u.kota || "—"}</td>
                    <td className="p-3">{u.telpon || "—"}</td>
                    <td className="p-3">{u.wa || "—"}</td>
                    <td className="p-3 max-w-xs truncate text-gray-700">
                      {u.email || "—"}
                    </td>
                    <td className="p-3 max-w-xs truncate">
                      {u.web ? (
                        <a
                          href={`https://${u.web}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {u.web}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap text-right">
                      <button
                        onClick={() => openEdit(u)}
                        className="inline-block px-2 py-1 text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => deleteUser(u.userid)}
                        className="inline-block px-2 py-1 text-red-600 hover:text-red-800"
                        title="Hapus"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ──────────────────────────────────────────────── */}
      {/*               Edit Modal (Material style)         */}
      {/* ──────────────────────────────────────────────── */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-xl font-semibold">
                Edit Pengguna — {editingUser.userid}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
              {[
                { label: "Nama Lengkap",    name: "nama",    type: "text" },
                { label: "Level / Role",    name: "level",   type: "text" },
                { label: "Kota / Kabupaten",name: "kota",    type: "text" },
                { label: "Nomor Telepon",   name: "telpon",  type: "tel"  },
                { label: "Nomor WhatsApp",  name: "wa",      type: "tel"  },
                { label: "Alamat Email",    name: "email",   type: "email"},
                { label: "Alamat Lengkap",  name: "alamat",  type: "text", span: true },
                { label: "Website / Link",  name: "web",     type: "url",  span: true },
              ].map(({ label, name, type, span }) => (
                <div key={name} className={span ? "md:col-span-2" : ""}>
                  <label className="block mb-1.5 text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  <input
                    type={type}
                    name={name}
                    value={editingUser[name] ?? ""}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-4 border-t px-6 py-4">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Batal
              </button>
              <button
                onClick={saveEdit}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                <Check size={18} /> Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUser;